import React, { useState, useEffect } from 'react';
import { getToken } from '../../../../services/api';

const AssignmentModal = ({ estado, onClose, onAssignmentComplete }) => {
  const [precatorios, setPrecatorios] = useState([]);
  const [gestores, setGestores] = useState([]);
  const [selectedGestores, setSelectedGestores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState(null);
  const [distributionPreview, setDistributionPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    fetchData();
  }, [estado]);

  useEffect(() => {
    if (selectedGestores.length > 0 && precatorios.length > 0) {
      fetchDistributionPreview();
    } else {
      setDistributionPreview(null);
    }
  }, [selectedGestores, precatorios]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_VERCEL_API_URL : process.env.REACT_APP_LOCAL_API_URL;
      
      // Fetch both precatorios and gestores in parallel
      const [precatoriosRes, gestoresRes] = await Promise.all([
        fetch(`${API_URL}/precatorio-assignments/unassigned/${estado}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/precatorio-assignments/gestores-comerciais`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!precatoriosRes.ok || !gestoresRes.ok) {
        throw new Error('Erro ao buscar dados');
      }

      const precatoriosData = await precatoriosRes.json();
      const gestoresData = await gestoresRes.json();

      setPrecatorios(precatoriosData);
      setGestores(gestoresData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDistributionPreview = async () => {
    try {
      setLoadingPreview(true);
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_VERCEL_API_URL : process.env.REACT_APP_LOCAL_API_URL;
      
      const response = await fetch(`${API_URL}/precatorio-assignments/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          precatorioIds: precatorios.map(p => p._id),
          gestorIds: selectedGestores,
          estado: estado
        })
      });

      if (response.ok) {
        const preview = await response.json();
        setDistributionPreview(preview);
      }
    } catch (err) {
      console.error('Error fetching preview:', err);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleGestorToggle = (gestorId) => {
    setSelectedGestores(prev => {
      if (prev.includes(gestorId)) {
        return prev.filter(id => id !== gestorId);
      } else {
        return [...prev, gestorId];
      }
    });
  };

  const handleAssign = async () => {
    if (selectedGestores.length === 0 || precatorios.length === 0) return;

    try {
      setAssigning(true);
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_VERCEL_API_URL : process.env.REACT_APP_LOCAL_API_URL;
      
      const response = await fetch(`${API_URL}/precatorio-assignments/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          precatorioIds: precatorios.map(p => p._id),
          gestorIds: selectedGestores,
          estado: estado
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Erro ao distribuir precatórios');
      }

      const result = await response.json();
      alert(result.message);
      onAssignmentComplete();
    } catch (err) {
      alert('Erro: ' + err.message);
    } finally {
      setAssigning(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  return (
    <div className="assignment-modal-overlay" onClick={onClose}>
      <div className="assignment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="assignment-modal-header">
          <h2>Distribuir Precatórios - {estado}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="assignment-modal-body">
          {loading ? (
            <div className="loading">Carregando...</div>
          ) : error ? (
            <div className="error">Erro: {error}</div>
          ) : (
            <>
              <div className="precatorios-info">
                <p><strong>{precatorios.length}</strong> precatórios selecionados para distribuição</p>
                <p>Valor total: <strong>{formatCurrency(precatorios.reduce((sum, p) => sum + (p.valor_atualizado || 0), 0))}</strong></p>
                
                {precatorios.length > 0 && (
                  <div className="precatorios-list">
                    {precatorios.slice(0, 5).map((precatorio, index) => (
                      <div key={precatorio._id} className="precatorio-item">
                        <strong>{precatorio.numero_precatorio}</strong> - {precatorio.ente} - {formatCurrency(precatorio.valor_atualizado)}
                      </div>
                    ))}
                    {precatorios.length > 5 && (
                      <div className="precatorio-item">
                        ... e mais {precatorios.length - 5} precatórios
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="gestores-section">
                <h3>Selecione os Gestores Comerciais:</h3>
                {gestores.length === 0 ? (
                  <p>Nenhum gestor comercial disponível</p>
                ) : (
                  <div className="gestores-list">
                    {gestores.map(gestor => (
                      <label key={gestor._id} className={`gestor-item ${selectedGestores.includes(gestor._id) ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selectedGestores.includes(gestor._id)}
                          onChange={() => handleGestorToggle(gestor._id)}
                        />
                        <div className="gestor-info">
                          <span className="name">{gestor.name}</span>
                          <span className="email">{gestor.email}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {distributionPreview && !loadingPreview && selectedGestores.length > 0 && (
                <div className="distribution-preview">
                  <h3>Prévia da Distribuição:</h3>
                  <div className="preview-content">
                    <p className="total-groups">
                      <strong>{distributionPreview.totalGroups}</strong> grupos de CPF/CNPJ serão distribuídos
                    </p>
                    <div className="preview-list">
                      {distributionPreview.preview.map((item, index) => (
                        <div key={item.gestorId} className="preview-item">
                          <span className="gestor-name">{item.gestorName}</span>
                          <span className="group-count">receberá <strong>{item.groupCount}</strong> grupos de CPF/CNPJ</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {loadingPreview && (
                <div className="distribution-preview">
                  <p className="loading-preview">Calculando distribuição...</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="assignment-modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={assigning}>
            Cancelar
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleAssign}
            disabled={selectedGestores.length === 0 || precatorios.length === 0 || assigning}
          >
            {assigning ? 'Distribuindo...' : 'Distribuir Precatórios'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignmentModal;
